import { itemsApi } from "#/api/itemsApi";
import { ItemType } from "#/types/items";
import { productApi } from "#/api/productApi";
import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import Popover from "#/components/overlays/Popover";
import type { TableSortProps } from "#/components/tables/BaseTable";
import SelectableTable from "#/components/tables/SelectableTable";
import { getRandomColor, getItemStatusColor } from "#/utils/ColorUtils";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { exportToCSV } from "#/utils/CsvUtils";
import { Badge, Button, Select, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDownload,
  IconExternalLink,
  IconFilter,
  IconEye,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import CreateTicketDrawer from "./components/CreateTicketDrawer";
import ImportCSVModal from "./components/ImportCSVModal";
import NotifyPopover from "#/components/overlays/NotifyPopover";
import SelectTemplateModal from "./components/SelectTemplateModal";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import BulkDeleteModal from "./components/BulkDeleteModal";
import BulkUpdateStatusModal from "./components/BulkUpdateStatusModal";

export interface Props {
  withoutActions?: boolean;
}

const ItemsPage = ({ withoutActions = false }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<string | undefined>(searchParams.get('status') || undefined);
  const [categoryId, setCategoryId] = useState<string | undefined>(searchParams.get('categoryId') || undefined);
  const [siteId, setSiteId] = useState<string | undefined>(searchParams.get('siteId') || undefined);
  const [itemType, setItemType] = useState<string | undefined>(searchParams.get('itemType') || undefined);
  const [search, setSearch] = useState<string>(searchParams.get('search') || "");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  // Update URL params when filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (categoryId) params.categoryId = categoryId;
    if (siteId) params.siteId = siteId;
    if (itemType) params.itemType = itemType;
    if (search) params.search = search;
    setSearchParams(params);
  }, [status, categoryId, siteId, itemType, search, setSearchParams]);

  const [
    createMenuPopoverOpened,
    {
      open: openCreateMenuPopover,
      close: closeCreateMenuPopover,
      toggle: toggleCreateMenuPopover,
    },
  ] = useDisclosure(false);

  const [
    createTicketDrawerOpened,
    { open: openCreateTicketDrawer, close: closeCreateTicketDrawer },
  ] = useDisclosure(false);

  const [
    importCsvModalOpened,
    {
      open: openImportCsvModal,
      close: closeImportCsvModal,
      toggle: toggleImportCsvModal,
    },
  ] = useDisclosure(false);

  const [
    filtersOpened,
    { open: openFilters, close: closeFilters, toggle: toggleFilters },
  ] = useDisclosure(false);

  const [
    templateModalOpened,
    { open: openTemplateModal, close: closeTemplateModal },
  ] = useDisclosure(false);

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [
    bulkUpdateStatusModalOpened,
    { open: openBulkUpdateStatusModal, close: closeBulkUpdateStatusModal },
  ] = useDisclosure(false);

  const [
    bulkDeleteModalOpened,
    { open: openBulkDeleteModal, close: closeBulkDeleteModal },
  ] = useDisclosure(false);

  const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const headers = [
    { key: "createdAt", value: t("items.columns.dateOfCreation") },
    { key: "external_id", value: t("items.columns.externalId") },
    { key: "last_scan", value: t("items.columns.lastScan") },
    { key: "item_type", value: t("items.columns.type") },
    { key: "id", value: t("items.columns.rfidTags") },
    { key: "status", value: t("items.columns.status") },
    { key: "type", value: t("items.columns.category") },
    { key: "location", value: t("items.columns.location") },
    { key: "stolen", value: t("items.columns.stolen") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("last_scan");

  const prepareExportData = (itemsToExport: typeof items) => {
    return itemsToExport.map((item) => ({
      createdAt: formatDateTimeForExport(item.createdAt),
      external_id: item.external_id || "",
      last_scan: formatDateTimeForExport(item.last_scan_data?.time),
      last_scan_type: item.last_scan_data?.type || "",
      item_type: item.type ? sanitize(item.type) : "Item",
      rfid_tags: item.rfid_tags?.map((tag) => tag.rfid).join("; ") || "",
      status: sanitize(item.status) || "",
      product: item.product?.name || "",
      location: item.location || "",
      stolen: item.stolen ? "Yes" : "No",
    }));
  };

  const exportHeaders = [
    "createdAt",
    "external_id",
    "last_scan",
    "last_scan_type",
    "item_type",
    "rfid_tags",
    "status",
    "product",
    "location",
    "stolen",
  ] as const;

  const exportHeaderLabels: Record<string, string> = {
    createdAt: t("items.columns.dateOfCreation"),
    external_id: t("items.columns.externalId"),
    last_scan: t("items.columns.lastScan"),
    last_scan_type: t("items.columns.lastScanType"),
    item_type: t("items.columns.type"),
    rfid_tags: t("items.columns.rfidTags"),
    status: t("items.columns.status"),
    product: t("items.columns.category"),
    location: t("items.columns.location"),
    stolen: t("items.columns.stolen"),
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const note = t("common.utcNote");
    const filename = `items-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, [...exportHeaders], filename, exportHeaderLabels, note);
  };

  const handleExportAll = async () => {
    try {
      const filters = [];
      if (status) filters.push(`status=${status}`);
      if (categoryId) filters.push(`product_id=${categoryId}`);
      if (siteId) filters.push(`site_id=${siteId}`);
      if (itemType) filters.push(`type=${itemType}`);

      const response = await itemsApi.getAll({
        page: 1,
        limit: total,
        sortBy: sortBy,
        order: sortDirection,
        ...(filters.length > 0 ? { filterBy: filters.join(",") } : {}),
      });

      const allItems = response.data.data;
      const exportData = prepareExportData(allItems);

      const note = t("common.utcNote");
      const filename = `items-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, [...exportHeaders], filename, exportHeaderLabels, note);
    } catch (error) {
      console.error("Failed to export all items:", error);
    }
  };

  const { data: { products: categories = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await productApi.getAll({
        page: 1,
        limit: 1000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        products: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({
        page: 1,
        limit: 1000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isFetching,
    refetch: getItems,
  } = useQuery({
    queryKey: ["items", status, categoryId, siteId, itemType, sortDirection, sortBy, page, limit, debouncedSearch],
    queryFn: async () => {
      const filters = [];
      if (status) filters.push(`status=${status}`);
      if (categoryId) filters.push(`product_id=${categoryId}`);
      if (siteId) filters.push(`site_id=${siteId}`);
      if (itemType) filters.push(`type=${itemType}`);

      const response = await itemsApi.getAll({
        page: page,
        limit: limit,
        sortBy: sortBy,
        order: sortDirection,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(filters.length > 0 ? { filterBy: filters.join(",") } : {}),
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        items: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-2 relative">
        <div className="w-full flex flex-col gap-3">
          <div className="w-full flex  items-center justify-between">
            <Title order={4}>{t("items.title")}</Title>
            <div className="gap-2 flex items-center ml-auto">
              <Popover
                classNames={{
                  overlay: "backdrop-blur-none",
                }}
                opened={createMenuPopoverOpened}
                onChange={toggleCreateMenuPopover}
                anchor={
                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      leftSection={<IconPlus size={18} />}
                      color="black"
                      onClick={() => openCreateMenuPopover()}
                      disabled={!canWrite()}
                    >
                      {t("common.create")}
                    </Button>
                  </PermissionTooltip>
                }
              >
                <div className="flex flex-col gap-2">
                  <Tooltip
                    label={!canWrite() ? t("common.youDontHavePermission") : t("items.selectAtLeastOne")}
                    withArrow
                    disabled={selectedItemsIds.length > 0 && canWrite()}
                  >
                    <Button
                      leftSection={<IconPlus size={18} />}
                      variant="outline"
                      onClick={() => {
                        closeCreateMenuPopover();
                        openCreateTicketDrawer();
                      }}
                      disabled={!selectedItemsIds || selectedItemsIds.length <= 0 || !canWrite()}
                      color="black"
                    >
                      {t("items.createTicket")}
                    </Button>
                  </Tooltip>

                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      color="black"
                      leftSection={<IconPlus size={18} />}
                      onClick={() => {
                        void navigate(`/inventory/items/new`);
                      }}
                      disabled={!canWrite()}
                    >
                      {t("items.createAnItem")}
                    </Button>
                  </PermissionTooltip>

                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      color="black"
                      variant="outline"
                      leftSection={<IconPlus size={18} />}
                      onClick={() => {
                        closeCreateMenuPopover();
                        openTemplateModal();
                      }}
                      disabled={!canWrite()}
                    >
                      {t("items.createFromTemplate")}
                    </Button>
                  </PermissionTooltip>
                </div>
              </Popover>
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  leftSection={<IconUpload size={18} />}
                  color="green.5"
                  onClick={() => openImportCsvModal()}
                  disabled={!canWrite()}
                >
                  {t("common.importCsv")}
                </Button>
              </PermissionTooltip>
              <Button
                leftSection={<IconDownload size={18} />}
                variant="outline"
                onClick={openExportModal}
                disabled={!items || items.length === 0}
              >
                {t("common.exportCsv")}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-between">
            <TextInput
              className="flex-1 max-w-96"
              placeholder={t("items.searchPlaceholder")}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
            />
            <div className="flex items-center gap-2 ml-auto">
              <Popover
              opened={filtersOpened}
              onChange={toggleFilters}
              anchor={
                <Button
                  leftSection={<IconFilter size={18} />}
                  variant={status || categoryId || siteId || itemType ? "filled" : "outline"}
                  color="black"
                  onClick={openFilters}
                >
                  {t("common.filters")}
                </Button>
              }
            >
              <div className="flex flex-col gap-4 min-w-64">
                <Select
                  label={t("items.filters.status")}

                  placeholder={t("common.selectStatus")}
                  value={status}
                  onChange={(value) => {
                    setPage(1);
                    setStatus(value ?? undefined);
                  }}
                  data={allowedStatuses.map((item) => ({
                    value: item,
                    label: sanitizeItemStatusLabel(item),
                  }))}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.category")}

                  placeholder={t("items.form.categoryPlaceholder")}
                  value={categoryId}
                  onChange={(value) => {
                    setPage(1);
                    setCategoryId(value ?? undefined);
                  }}
                  data={categories.map((category) => ({
                    value: category.id.toString(),
                    label: category.name,
                  }))}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.site")}

                  placeholder={t("common.selectSite")}
                  value={siteId}
                  onChange={(value) => {
                    setPage(1);
                    setSiteId(value ?? undefined);
                  }}
                  data={sites.map((site) => ({
                    value: site.id.toString(),
                    label: site.name,
                  }))}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.type")}

                  placeholder={t("items.filters.type")}
                  value={itemType}
                  onChange={(value) => {
                    setPage(1);
                    setItemType(value ?? undefined);
                  }}
                  data={[
                    { value: "item", label: sanitize("item") },
                    { value: "cage", label: sanitize("cage") },
                    { value: "cage_item", label: sanitize("cage_item") },
                  ]}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Button
                  fullWidth
                  color="black"
                  variant="outline"
                  disabled={!status && !categoryId && !siteId && !itemType}
                  onClick={() => {
                    setPage(1);
                    setStatus(undefined);
                    setCategoryId(undefined);
                    setSiteId(undefined);
                    setItemType(undefined);
                    closeFilters();
                  }}
                >
                  {t("items.filters.clearAll")}
                </Button>
              </div>
            </Popover>
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
            </div>
          </div>
        </div>

      <div className="flex-1 overflow-auto">
        <SelectableTable
          headers={headers.map((item) => item.value)}
          loading={isFetching}
          selectedIndices={selectedIndices}
          onSelectionChange={(newIndices) => {
            const selectedItems = newIndices.map((index) => items[index]);
            const ids = selectedItems.map((item) => item.id);
            setSelectedItemsIds(ids);
            setSelectedIndices(newIndices);
          }}
          allowSorting
          sortableColumns={[1, 2, 5, 6, 9]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[withoutActions ? e.index : e.index - 1].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: withoutActions ? 0 : 1,
          }}
          disabledSelection={withoutActions}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td className="min-w-[140px]">
                  {item.createdAt ? (
                    <span>
                      {formatDate(item.createdAt, client)}
                      <br />
                      {formatTime(item.createdAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>

                <Table.Td className="min-w-[140px]">
                  {item.external_id || "--"}
                </Table.Td>

                <Table.Td className="min-w-[140px]">
                  {item.last_scan_data && item.last_scan_data.time ? (
                    <div className="w-full flex flex-col">
                      <span>
                        {formatDate(item.last_scan_data.time, client)}
                        <br />
                        {formatTime(item.last_scan_data.time, client)}
                      </span>
                      <div className="w-full -mt-1  flex mt-2">
                        <Badge
                          color={item.last_scan_data.type ? "lime" : "grape"}
                          variant="outline"
                          size="sm"
                          className="mt-2"
                        >
                          {item.last_scan_data.type}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    "--"
                  )}
                </Table.Td>

                <Table.Td className="min-w-[130px]">
                  <div className="flex flex-col items-start">
                    <Badge
                      color={
                        item.type === ItemType.CAGE
                          ? "violet"
                          : item.type === ItemType.CAGE_ITEM
                            ? "cyan"
                            : "gray"
                      }
                      variant="light"
                      size="sm"
                    >
                      {item.type ? sanitize(item.type) : sanitize("item")}
                    </Badge>
                    {item.type === ItemType.CAGE_ITEM && item.cage && (
                      <Button
                        variant="transparent"
                        size="compact-xs"
                        color="black"
                        className="underline px-0 mt-2"
                        rightSection={<IconExternalLink size={12} />}
                        onClick={() => window.open(`/inventory/items/${item?.cage?.id}`, '_blank')}
                      >
                        View Cage
                      </Button>
                    )}
                  </div>
                </Table.Td>

                <Table.Td className="min-w-[180px]">
                  {item.rfid_tags && item.rfid_tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      <Badge
                        color={getRandomColor(item.rfid_tags[0].rfid)}
                        variant="light"
                        size="sm"
                      >
                        {item.rfid_tags[0].rfid}
                      </Badge>
                      {item.rfid_tags.length > 1 && (
                        <Popover
                          withArrow
                          withOverlay={true}
                          anchor={
                            <Badge
                              color="gray"
                              variant="white"
                              size="sm"
                              className="cursor-pointer"
                            >
                              +{item.rfid_tags.length - 1} more
                            </Badge>
                          }
                        >
                          <div className="flex flex-col gap-2 ">
                            <span className="text-xs font-semibold text-slate-600">
                              All Tags ({item.rfid_tags.length})
                            </span>
                            <div className="flex flex-col gap-1 max-w-sm">
                              {item.rfid_tags.map((tag) => (
                                <Badge
                                  key={tag.id}
                                  color={getRandomColor(tag.rfid)}
                                  variant="dot"
                                  size="md"
                                  className="font-mono"
                                >
                                  {tag.rfid}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </Popover>
                      )}
                    </div>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td className="min-w-[150px]">
                  {item?.status ? (
                    <Badge
                      color={getItemStatusColor(item.status)}
                      variant="light"
                      size="md"
                    >
                      {sanitize(item.status)}
                    </Badge>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td className="min-w-[150px]">
                  {item?.product?.name ? (
                    <Badge
                      color={getRandomColor(item.product.name)}
                      variant="light"
                      size="md"
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(`/inventory/categories/${item.product.id}`, '_blank')}
                      rightSection={<IconExternalLink size={12} className="opacity-60" />}
                    >
                      {item.product.name}
                    </Badge>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td className="min-w-[200px]">{item.location ?? "--"}</Table.Td>
                <Table.Td className="min-w-[80px] capitalize">
                  {item.stolen ? t("common.yes") : t("common.no")}
                </Table.Td>
                <Table.Td className="min-w-[120px]">
                  <div className="grid grid-cols-2 gap-4">
                    <NotifyPopover item={item} />
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/inventory/items/${item.id}`);
                      }}
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <CreateTicketDrawer
        itemIds={selectedItemsIds}
        opened={createTicketDrawerOpened}
        onClose={() => closeCreateTicketDrawer()}
        onSuccess={() => {
          closeCreateTicketDrawer();
          setSelectedItemsIds([]);
          setSelectedIndices([]);
        }}
      />

      <ImportCSVModal
        opened={importCsvModalOpened}
        onClose={() => closeImportCsvModal()}
        onChange={toggleImportCsvModal}
        onSuccess={() => {
          closeImportCsvModal();
          void getItems();
        }}
      />

      <SelectTemplateModal
        opened={templateModalOpened}
        onClose={closeTemplateModal}
      />

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />

      <BulkUpdateStatusModal
        opened={bulkUpdateStatusModalOpened}
        onClose={closeBulkUpdateStatusModal}
        selectedItemIds={selectedItemsIds}
        onSuccess={() => {
          setSelectedItemsIds([]);
          setSelectedIndices([]);
          void getItems();
        }}
      />

      <BulkDeleteModal
        opened={bulkDeleteModalOpened}
        onClose={closeBulkDeleteModal}
        selectedItemIds={selectedItemsIds}
        onSuccess={() => {
          setSelectedItemsIds([]);
          setSelectedIndices([]);
          void getItems();
        }}
      />

      {selectedItemsIds.length > 0 && (
        <div className="absolute bottom-0 left-0 inset-x-0 z-50 flex items-center justify-between w-full bg-black/10 border-t border-black/50 backdrop-blur-sm p-4">
          <div className="flex items-center gap-2">
            <Text size="md" fw={600} c="dark">
              {selectedItemsIds.length} {t("common.selected")}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                leftSection={<IconRefresh size={16} />}
                color="black"
                variant="filled"
                size="md"
                onClick={openBulkUpdateStatusModal}
                disabled={!canWrite()}
              >
                {t("items.updateStatus")}
              </Button>
            </PermissionTooltip>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="filled"
                size="md"
                onClick={openBulkDeleteModal}
                disabled={!canDelete()}
              >
                {t("common.delete")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsPage;
